<template>
    <div class="w-100 align-self-start">
        <v-card flat color="transparent">
            <v-card-title class="d-block">
                <v-btn
                    class="mb-3"
                    small
                    text
                    color="secondary"
                    :to="{
                        name: 'snapshot-applications',
                        params: { oid: $route.params.oid, sid: $route.params.sid, iid: $route.params.iid, snid: $route.params.snid }
                    }">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Applications
                </v-btn>
                <div>Application sessions</div>
            </v-card-title>
            <v-card-subtitle class="d-flex align-center">
                <img width="18" height="18" :src="appIcon(selectedApp)" class="mr-2" />
                {{ selectedApp.long_id }}
            </v-card-subtitle>
            <v-card-text>
                <v-skeleton-loader v-if="loading" class="mx-auto" type="table"></v-skeleton-loader>
                <v-alert v-else-if="error" type="error" text class="mt-3">
                    {{ error }}
                </v-alert>
                <template v-else>
                    <div class="d-flex align-center">
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" class="mr-2" color="primary">
                                    Columns
                                    <v-icon right>mdi-chevron-down</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item-group v-model="selectedHeaders" multiple>
                                    <v-list-item v-for="column in orderedHeaders" :key="column.column_id" :value="column">
                                        <template v-slot:default="{ active }">
                                            <v-list-item-action>
                                                <v-checkbox :input-value="active"></v-checkbox>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>{{ column.text }}</v-list-item-title>
                                            </v-list-item-content>
                                        </template>
                                    </v-list-item>
                                </v-list-item-group>
                            </v-list>
                        </v-menu>
                        <v-spacer></v-spacer>
                        <v-text-field
                            prepend-inner-icon="mdi-magnify "
                            autofocus
                            hide-details
                            solo
                            flat
                            dense
                            placeholder="Search for a session..."
                            v-model="search"
                            clearable
                            class="mr-2"></v-text-field>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon v-on="on" :loading="loading" @click="fetchSessions()">
                                    <v-icon>refresh</v-icon>
                                </v-btn>
                            </template>
                            <span>Refresh sessions</span>
                        </v-tooltip>
                    </div>
                    <v-data-table
                        :search="search"
                        :headers="filteredHeaders"
                        :items="sessions"
                        :options="tableOptions"
                        :loading="loading"
                        :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                        class="mt-4">
                        <template v-slot:top="{ options, pagination, updateOptions }">
                            <div class="d-flex align-center">
                                <v-spacer></v-spacer>
                                <v-data-footer
                                    :pagination="pagination"
                                    :options="options"
                                    @update:options="updateOptions"
                                    :itemsPerPageOptions="itemsPerPageOptions"
                                    items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                                    class="no-border" />
                            </div>
                        </template>
                        <template v-slot:no-data>This app has no sessions yet.</template>
                        <template v-slot:[`item.size`]="{ item }">
                            <app-scale-info-menu :app="item">
                                <v-btn text class="text-none secondary--text">
                                    <template v-if="item.active_resource === 'ncu'">
                                        <v-icon left color="primary">mdi-card-bulleted-outline</v-icon>
                                        {{ `${item.ncu} CU` }}
                                    </template>
                                    <template v-else-if="currentNodePool(item.node_pool)">
                                        <v-icon left color="purple" v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'cpu'">mdi-memory</v-icon>
                                        <v-icon left color="light-green" v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'gpu'">
                                            mdi-memory mdi-rotate-45
                                        </v-icon>
                                        <v-icon left color="orange" v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'ssd'">mdi-nas</v-icon>
                                        {{ `${getNodepoolPrimaryParameter(currentNodePool(item.node_pool))}` }}
                                    </template>
                                    <span v-else>Retired</span>
                                </v-btn>
                            </app-scale-info-menu>
                        </template>
                        <template v-slot:[`item.session_id`]="{ item }">
                            <div class="text-no-wrap">
                                {{ item.session_id }}
                            </div>
                        </template>
                        <template v-slot:[`item.credits_spent`]="{ item }">
                            <span v-if="item.credits_spent">{{ item.credits_spent.toFixed(2) }}</span>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:[`item.sidecar_ncu`]="{ item }">
                            <span v-if="item.sidecar_ncu">{{ item.sidecar_ncu }}</span>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:[`item.ncu_hour`]="{ item }">
                            <span v-if="item.ncu_hour">{{ item.ncu_hour }}</span>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:[`item.runtime`]="{ item }">
                            <div class="text-no-wrap">
                                <v-icon color="brown" small left>mdi-timer-sand</v-icon>
                                <span>{{ formatRuntime(item.runtime_seconds) }}</span>
                            </div>
                        </template>
                        <template v-slot:[`item.ncu_sidecars_total`]="{ item }">
                            <span v-if="item.ncu_sidecars_total !== null" class="text-no-wrap">
                                <v-icon left small color="primary">mdi-card-bulleted-outline</v-icon>
                                {{ item.ncu_sidecars_total }} CU
                            </span>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:[`item.ncu_hours_used`]="{ item }">
                            <span v-if="item.ncu_hours_used !== null">{{ item.ncu_hours_used.toFixed(2) }}</span>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:[`item.start_time`]="{ item }">
                            <div class="text-no-wrap">
                                <v-icon small color="success" left>mdi-play</v-icon>
                                <span>{{ item.start_time | dateTimeToHuman }}</span>
                            </div>
                        </template>
                        <template v-slot:[`item.stop_time`]="{ item }">
                            <div class="d-flex align-center text-no-wrap">
                                <v-icon small color="warning" left>mdi-stop</v-icon>
                                <span v-if="item.stop_time">{{ item.stop_time | dateTimeToHuman }}</span>
                                <v-progress-linear v-else indeterminate class="mr-2"></v-progress-linear>
                            </div>
                        </template>
                        <template v-slot:[`item.start_uid_full_name`]="{ item }">
                            <v-chip color="grey lighten-3">
                                <v-icon small color="success" left>mdi-play</v-icon>
                                <span>{{ item.start_uid_full_name }}</span>
                            </v-chip>
                        </template>
                        <template v-slot:[`item.stop_uid_full_name`]="{ item }">
                            <v-chip color="grey lighten-3" v-if="item.stop_uid_full_name">
                                <v-icon small color="warning" left>mdi-stop</v-icon>
                                <span>{{ item.stop_uid_full_name }}</span>
                            </v-chip>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <v-menu
                                v-model="dropdownMenu[`session_${item.session_id}`]"
                                left
                                offset-x
                                nudge-top="10"
                                v-if="item.can_read_logs && item?.logging_containers?.length > 0">
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" text color="secondary" small>
                                        <v-icon>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list nav dense>
                                    <v-list-item @click="viewLogs(item)">
                                        <v-list-item-icon>
                                            <v-icon color="secondary">mdi-code-tags</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="secondary--text mr-5">See Logs</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table>
                </template>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { scaling } from '@/mixins/scaling'
import { enumsData } from '@/mixins/enums'
import { Duration } from 'luxon'
import { orderBy } from 'lodash'
import { appTypeAndImageLink } from '@/mixins/appTypeAndImage'

const AppScaleInfoMenu = () => import('@/components/AppScaleInfoMenu.vue')

export default {
    name: 'AppSessions',
    components: { AppScaleInfoMenu },
    mixins: [scaling, enumsData, appTypeAndImageLink],
    data() {
        return {
            sessions: [],
            loading: false,
            dropdownMenu: {},
            selectedApp: null,
            search: '',
            error: '',
            selectedHeaders: [],
            headers: [
                { text: 'Session id', value: 'session_id', visible: false },
                { text: 'Start time', value: 'start_time', visible: true },
                { text: 'Stop time', value: 'stop_time', visible: true },
                { text: 'Runtime', value: 'runtime', visible: true },
                { text: 'Size', value: 'size', visible: true, class: 'pl-8' },
                { text: 'Credits spent', value: 'credits_spent', visible: true },
                { text: 'Start user', value: 'start_uid_full_name', visible: false },
                { text: 'Stop user', value: 'stop_uid_full_name', visible: false },
                { text: 'Sidecar size', value: 'ncu_sidecars_total', visible: false },
                { text: 'NCU hour', value: 'ncu_hours_used', visible: false }
            ],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            },
            itemsPerPageOptions: [25, 50, 100, -1]
        }
    },
    created() {
        this.$store.dispatch('appStore/fetchNodePools')
        this.fetchSessions()
        this.selectedHeaders = this.headers.filter(header => header.visible)
    },

    computed: {
        ...mapGetters('spaceStore', ['currentSpaceType']),
        ...mapState('snapshotStore', ['applications', 'applicationsFetching']),
        filteredHeaders() {
            return this.headers.filter(header => this.selectedHeaders.includes(header))
        },
        orderedHeaders() {
            return orderBy(this.headers, 'value')
        }
    },
    methods: {
        formatRuntime(seconds) {
            if (!seconds) return '-'
            const duration = Duration.fromObject({ seconds }).toFormat('hh:mm:ss')
            return duration
        },
        async fetchSessions() {
            this.error = ''
            this.loading = true
            try {
                const { data } = await this.$axios.get(`/applications/${this.$route.params.aid}/sessions`)
                this.sessions = data
            } catch (error) {
                this.error = error.message
            }
            this.loading = false
        },
        viewLogs(session) {
            this.$router.push({
                name: 'session-logs',
                params: { aid: this.$route.params.aid, sessId: session.session_id }
            })
        }
    },
    watch: {
        applicationsFetching: {
            handler(fetching) {
                if (!fetching) {
                    this.selectedApp = this.applications.find(app => app.aid === Number(this.$route.params.aid))
                }
            },
            immediate: true
        },
        selectedHeaders() {
            this.filteredHeaders.push({ text: 'Actions', align: 'right', value: 'actions', sortable: false, visible: true })
        }
    }
}
</script>
